import React, { useRef } from 'react';
import { Button, Dropdown } from 'antd';
import styles from '../../styles/ads.module.scss';
import PropTypes from 'prop-types';

const GenericTable = ({
  data = [],
  columns = [],
  actions = [],
  emptyState = {
    icon: '/assets/icons/no-data.svg',
    title: 'Trenutno nema podataka',
  },
  grayTheme = false,
  mobNoScroll = false,
  rowClassName = () => '',
  rowSelection = null,
}) => {
  const selectAllRef = useRef(null);

  const selectableRows = rowSelection
    ? data.filter((row) => !rowSelection.getCheckboxProps?.(row)?.disabled)
    : [];
  const selectedRowKeys = rowSelection?.selectedRowKeys || [];
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedRowKeys.includes(row._id));
  const someSelected =
    !allSelected &&
    selectableRows.some((row) => selectedRowKeys.includes(row._id));

  if (selectAllRef.current) {
    selectAllRef.current.indeterminate = someSelected;
  }

  const handleToggleAll = () => {
    if (!rowSelection) return;
    if (allSelected) {
      rowSelection.onChange(
        selectedRowKeys.filter(
          (key) => !selectableRows.some((row) => row._id === key)
        )
      );
    } else {
      const additions = selectableRows
        .map((row) => row._id)
        .filter((key) => !selectedRowKeys.includes(key));
      rowSelection.onChange([...selectedRowKeys, ...additions]);
    }
  };

  const handleToggleRow = (row) => {
    if (!rowSelection) return;
    if (selectedRowKeys.includes(row._id)) {
      rowSelection.onChange(selectedRowKeys.filter((key) => key !== row._id));
    } else {
      rowSelection.onChange([...selectedRowKeys, row._id]);
    }
  };

  return (
    <div className="table-wrapper">
      <table
        style={{ width: '100%' }}
        className={`${styles.table} ${grayTheme ? 'grayTheme' : ''} ${
          mobNoScroll ? 'mobNoScroll' : ''
        }`}
      >
        <thead>
          <tr>
            {rowSelection && (
              <th>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  disabled={selectableRows.length === 0}
                  onChange={handleToggleAll}
                />
              </th>
            )}
            {columns.map((col) => (
              <th key={col.key} className={`${col.align} ${col.key}`}>
                {col.label}
              </th>
            ))}
            {actions.length > 0 && <th></th>}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 &&
            data.map((row) => (
              <tr key={row._id} className={rowClassName(row)}>
                {rowSelection && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRowKeys.includes(row._id)}
                      onChange={() => handleToggleRow(row)}
                      {...(rowSelection.getCheckboxProps?.(row) || {})}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className={`${col.align} ${col.key}`}>
                    {typeof col.customCell === 'function'
                      ? col.customCell(row)
                      : typeof col.render === 'function'
                        ? col.render(row[col.key], row)
                        : row[col.key]}
                  </td>
                ))}
                {actions.length > 0 && (
                  <td className="alignRight">
                    <Dropdown
                      menu={{
                        title: 'Opcije',
                        items: actions
                          .filter((action) =>
                            action.visible ? action.visible(row) : true
                          )
                          .map((action) => {
                            const { visible, ...rest } = action;
                            return {
                              ...rest,
                              label:
                                typeof rest.label === 'function'
                                  ? rest.label(row)
                                  : rest.label,
                              disabled:
                                typeof rest.disabled === 'function'
                                  ? rest.disabled(row)
                                  : (rest.disabled ?? false),
                              onClick: () => rest.onClick(row),
                            };
                          }),
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      overlayClassName="table-actions-dropdown"
                    >
                      <Button className="btn-noBorder btn-menu">
                        <img src="/assets/icons/dots.svg" alt="Dots" />
                      </Button>
                    </Dropdown>
                  </td>
                )}
              </tr>
            ))}
          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1 + (rowSelection ? 1 : 0)}
                style={{ textAlign: 'center', padding: 8 }}
                // className={}
              >
                <div className={styles.noData}>
                  {/* <img src={emptyState.icon} alt="No data" /> */}
                  <p style={{ margin: '20px 0' }} className="no-data-paragraph">
                    {emptyState.title}
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

GenericTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
    })
  ).isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      className: PropTypes.string,
      danger: PropTypes.bool,
      disabled: PropTypes.bool,
      onClick: PropTypes.func.isRequired,
    })
  ),
  emptyState: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    actionButton: PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      className: PropTypes.string,
      type: PropTypes.string,
      style: PropTypes.object,
    }),
  }),
  rowKey: PropTypes.string,
  grayTheme: PropTypes.bool,
  mobNoScroll: PropTypes.bool,
  rowClassName: PropTypes.func,
  rowSelection: PropTypes.shape({
    selectedRowKeys: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    getCheckboxProps: PropTypes.func,
  }),
};

GenericTable.defaultProps = {
  actions: [],
  emptyState: {
    icon: '/assets/icons/no-data.svg',
    title: 'No data available',
  },
  rowKey: '_id',
  grayTheme: false,
  mobNoScroll: false,
  rowClassName: () => '',
};

export default GenericTable;
